import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ghostRowIndex, useProjectOrder } from '../shared/order';
import { WriteOnly, useCanWrite } from '@core/auth/access';
import { data, useData, useSetting, useTable } from '@core/store';
import { addDays, daysBetween, mondayOf, sundayOf, today } from '@core/dates';
import { Button } from '@core/ui/Button';
import { ScreenLoading } from '@core/ui/Loading';
import {
  BAR_HEIGHT, LABEL_WIDTH, barGeometry, barHeightFor, dateAtX, snapDays, trackAtY, visibleSlots, gridOf,
} from '../shared/geometry';
import { useTitleMeasurer } from '../shared/textWidth';
import { dropTrack, planBacklog, planMove, planResize } from '../shared/planner';
import { projectRowShape } from './planner';
import { SelectionChip, applyGroupMove, groupShiftOf, taskMenu, useGroupActions, useMarquee, useSelection } from '../shared/selection';
import { applyPlanned } from '../shared/apply';
import { RangeControls } from '../shared/RangeControls';
import { defaultRange, startWithin, type Range } from '../shared/range';
import { ScreenToolbar, ViewMenu, barViewItems } from '../shared/Toolbar';
import { SpotlightChip, jumpToTask, spotBox, takeJump, useSpotlight } from '../shared/spotlight';
import { SplitGrid, type SplitRow } from '../shared/SplitGrid';
import { reorderGhostRow, withGhostRow } from '../shared/RowActions';
import type { BarLook, BarMarks } from '../shared/TaskBar';
import { RowGrid, RowLabel, type Drag, type NewTaskHint, type Preview, type QuickTask } from './ProjectsRow';
import { newDraft, planNewTask } from '../shared/cardEdits';
import { TaskModal } from '../shared/TaskModal';
import { SidePanel, SideTabs, useAgenda, type SideTab } from './SidePanel';
import { ProjectModal } from './ProjectModal';
import { newProjectDraft, projectDraftOf, type ProjectDraft } from './planner';
import { useContextBand } from '../shared/ContextBand';
import { useOpenQuestionCounts } from '../shared/questions';
import type { MenuItem } from '@core/ui/Menu';
import type { Row } from '@core/store/model';
import { planConfirm } from '../shared/series';
import { EventModal, eventDraftOf, newEventDraft, type EventDraft } from '../shared/EventModal';
import { VacationModal } from '../people/VacationModal';
import { newVacationDraft, vacationDraftOf, type VacationDraft } from '../people/planner';
import { keyEnd, keyStart, resizeTarget, snapStart } from '../shared/stepping';
import {
  usePendingIds, useDuplicate, useRowReorder, useTaskActions, useTaskCard, useTaskDrag, useTaskKeys, useToast, useTodayX, useHolidayDays, refocus, roadmapGroupPlan,
} from '../shared/interaction';
import {
  designNeeded, freshOf, rowKeyOf, rowTasksNow, taskNow, tasksNow, useAssignees, useAttention, useRoadmapTasks, useRows, type Task,
} from '../shared/data';


const focusOf = (id: string) => `[data-task="${id}"]`;

export function ProjectsScreen() {
  const status = useData((s) => s.status);
  const [range, setRange] = useSetting<Range>('projects.range', defaultRange());
  const [side, setSide] = useSetting<SideTab | null>('projects.side', null);
  const [hidden, setHidden] = useSetting<string[]>('projects.hidden', []);
  const allRows = useRows();
  const rows = useMemo(() => allRows.filter((r) => !hidden.includes(r.project_id)), [allRows, hidden]);
  const projectsTable = useTable('project');
  const hiddenProjects = useMemo(() => hidden.map((id) => projectsTable.get(id)).filter((p): p is NonNullable<typeof p> => !!p), [hidden, projectsTable]);
  const [projectDraft, setProjectDraft] = useState<ProjectDraft | null>(null);
  const [hoverBlock, setHoverBlock] = useState<string | null>(null); // проект, над строками которого курсор: действия видны у всего блока
  const canWrite = useCanWrite();
  const [hint, setHint] = useState<NewTaskHint | null>(null); // контур «New task here», пока открыто меню пустого места
  const [quick, setQuick] = useState<NewTaskHint | null>(null); // быстрая задача: контур остаётся, под ним микро-форма
  const subprojectsTable = useTable('subproject');
  const laneLinksTable = useTable('lane_project');
  const lanesTable = useTable('timeline_lane');
  const editProject = useCallback((id: string) => { const p = projectsTable.get(id); if (p) setProjectDraft(projectDraftOf(p, subprojectsTable.values(), laneLinksTable.values())); }, [projectsTable, subprojectsTable, laneLinksTable]);
  const { byRow, backlog, allTasks } = useRoadmapTasks();
  const { colors, names } = useAssignees();
  const attention = useAttention();
  const pendingIds = usePendingIds();
  const { fail } = useToast();
  const { draft, setDraft, openTask, closeModal, cardTab } = useTaskCard();
  const [badges, setBadges] = useSetting<boolean>('bars.badges', true);
  const [releaseMarks, setReleaseMarks] = useSetting<boolean>('bars.release_marks', true);
  // полные названия: строка растёт ровно настолько, чтобы влезло самое длинное видимое название
  const [fullTitles, setFullTitles] = useSetting<boolean>('bars.full_titles', false);
  const measure = useTitleMeasurer();
  const questionCounts = useOpenQuestionCounts();
  useEffect(() => { data.updatePresence({ screen: 'projects' }); }, []);

  const grid = useMemo(() => gridOf(range.from, range.to), [range]);
  const holidays = useHolidayDays(grid);
  const todayX = useTodayX(grid);
  const design = useMemo(() => designNeeded(allTasks.values(), range), [allTasks, range]);
  const projectNameOf = useCallback((t: Task) => projectsTable.get(t.project_id)?.name ?? '', [projectsTable]);
  const order = useProjectOrder();
  const projectOrderList = useMemo(() => order.rows.map((p) => ({ id: p.id, name: p.name })), [order.rows]);
  const { agenda, resolved, agendaMode, setAgendaMode, collapsed } = useAgenda(allTasks, projectOrderList);
  const [eventDraft, setEventDraft] = useState<EventDraft | null>(null);
  const [vacDraft, setVacDraft] = useState<VacationDraft | null>(null);
  const onEvent = useCallback((e: Parameters<typeof eventDraftOf>[0] | null, at = today()) => setEventDraft(e ? eventDraftOf(e) : newEventDraft(mondayOf(at), sundayOf(at))), []);
  const onConfirmEvent = useCallback((e: Row<'event'>) => { void data.applyChanges(planConfirm(e), { kind: 'confirm_event' }).then((r) => { if (!r.ok) fail(r.error); }); }, [fail]);
  const onVacation = useCallback((v: Parameters<typeof vacationDraftOf>[0] | null, at?: string) => setVacDraft(v ? vacationDraftOf(v) : newVacationDraft('', at)), []);
  const actions = useTaskActions(fail);
  const [hiddenLanes, setHiddenLanes] = useSetting<string[]>('context.hidden', []);
  const gridRef = useRef<HTMLDivElement>(null);
  const spotlight = useSpotlight();
  // прыжок к задаче: скрытый проект показывается, период сдвигается к задаче, если она за его краем (длина та же),
  // бар появляется через кадр — jumpToTask его дождётся
  const jump = useCallback((t: Task) => jumpToTask(gridRef, t.id, () => {
    if (t.project_id && hidden.includes(t.project_id)) setHidden(hidden.filter((x) => x !== t.project_id));
    if (t.start_date < range.from || t.end_date > range.to) {
      const from = addDays(mondayOf(t.start_date), -7);
      setRange({ from, to: addDays(from, daysBetween(range.from, range.to)) });
    }
  }), [hidden, setHidden, range, setRange]);
  const jumpById = useCallback((id: string) => { const t = taskNow(id); if (t) jump(t); }, [jump]);
  useEffect(() => { if (status !== 'ready') return; const id = takeJump(); if (id) jumpById(id); }, [status, jumpById]); // «Show on the grid» из истории
  const pinnedOpts = useMemo(() => ({ colors, onOpen: openTask, onUnpin: actions.pin, onJump: jump }), [colors, openTask, actions.pin, jump]);
  const context = useContextBand(grid, { todayX, onEvent, onConfirmEvent, onVacation, pinned: pinnedOpts, spotlight, hidden: hiddenLanes, setHidden: setHiddenLanes });

  const visibleTasksOf = useCallback((key: string) => (byRow.get(key) ?? []).filter((t) => barGeometry(t, grid) !== null), [byRow, grid]);
  const barHOf = useCallback((key: string) => (fullTitles ? barHeightFor(visibleTasksOf(key), grid, measure) : BAR_HEIGHT), [fullTitles, visibleTasksOf, grid, measure]);
  const latest = useRef({ rows, grid, visibleTasksOf, barHOf });
  latest.current = { rows, grid, visibleTasksOf, barHOf };
  const visibleIds = useMemo(() => new Set(rows.flatMap((r) => visibleTasksOf(r.key).map((t) => t.id))), [rows, visibleTasksOf]);
  const selection = useSelection(visibleIds, tasksNow);
  const { marquee } = useMarquee(gridRef, selection);

  // Цель перетаскивания под указателем: строка, дата левого края, трек (занятый → свободный).
  const targetFor = useCallback((d: Drag, clientX: number, clientY: number, dx: number): Preview | null => {
    const grid = gridRef.current;
    if (!grid) return null;
    const { rows, grid: g, visibleTasksOf, barHOf } = latest.current;
    const fresh = freshOf(d.task);
    const days = snapDays(dx, 1);
    if (d.kind !== 'move') return { kind: 'resize', ...resizeTarget(fresh, d.kind, days, d.day) };
    if (d.group.length > 1) {
      // группа: только время, строка и трек у каждого свои
      const start = snapStart(addDays(fresh.start_date, days), d.day);
      return { kind: 'move', rowKey: rowKeyOf(fresh.project_id, fresh.subproject_id), start_date: start, end_date: addDays(start, daysBetween(fresh.start_date, fresh.end_date)), track: fresh.track ?? 0 };
    }

    const el = document.elementFromPoint(clientX, clientY);
    const rowEl = el?.closest<HTMLElement>('[data-row]');
    const row = rows.find((r) => r.key === rowEl?.dataset['row']);
    if (!row || !rowEl) return null;
    let start: string | null;
    if (d.origin) {
      // из бэклога начала нет — дата под курсором
      const rect = grid.getBoundingClientRect();
      const at = dateAtX(Math.max(0, clientX - rect.left + grid.scrollLeft), g);
      start = at && snapStart(at, d.day);
    } else {
      start = snapStart(addDays(fresh.start_date, days), d.day);
    }
    if (!start) return null;
    const end = addDays(start, daysBetween(fresh.start_date, fresh.end_date));
    const slot = trackAtY(clientY - rowEl.getBoundingClientRect().top, barHOf(row.key));
    const track = dropTrack(visibleSlots(visibleTasksOf(row.key)), slot, rowTasksNow(row.key), start, end, fresh.id);
    return { kind: 'move', rowKey: row.key, start_date: start, end_date: end, track };
  }, []);

  // Записываем ровно то, что показывали: цель пересчитывается тем же кодом на отпускании.
  const finishDrag = useCallback(async (d: Drag, clientX: number, clientY: number, dx: number) => {
    if (d.kind === 'move' && document.elementFromPoint(clientX, clientY)?.closest('[data-backlog]')) {
      const r = await applyPlanned(() => ({ changes: tasksNow(d.group).flatMap((t) => planBacklog(t, true)), op: { kind: d.group.length > 1 ? 'to_backlog_many' : 'to_backlog', task_ids: d.group } }));
      if (!r.ok) fail(r.error);
      return;
    }
    const target = targetFor(d, clientX, clientY, dx);
    if (!target) return;
    if (target.kind === 'move' && d.group.length > 1) return applyGroupMove(d.task, d.group, target.start_date, fail);
    const r = await applyPlanned(() => {
      const t = freshOf(d.task);
      if (target.kind === 'move') {
        const row = latest.current.rows.find((r) => r.key === target.rowKey);
        if (!row) return { changes: [], op: { kind: 'noop' } };
        return {
          changes: planMove(t, { project_id: row.project_id, subproject_id: row.subproject_id, start_date: target.start_date, track: target.track }, rowTasksNow(row.key)),
          op: { kind: 'move_task', task_id: t.id },
        };
      }
      const side = d.kind === 'resize-end' ? 'end' : 'start';
      return { changes: planResize(t, side, side === 'end' ? target.end_date : target.start_date, rowTasksNow(rowKeyOf(t.project_id, t.subproject_id))), op: { kind: 'resize_task', task_id: t.id, side } };
    });
    if (!r.ok) fail(r.error);
  }, [targetFor, fail]);

  const { rowDrag, onHandlePointerDown } = useRowReorder({
    gridRef, rowAttr: 'data-row',
    targetIdOf: (key) => latest.current.rows.find((r) => r.key === key)?.project_id ?? null,
    blockSelector: (id) => `[data-row^="${id}:"]`,
    visibleIds: () => [...new Set(latest.current.rows.map((r) => r.project_id))],
    onDrop: (fromId, anchorId) => void order.reorder(fromId, anchorId).catch((e: Error) => fail(e.message)),
  });

  const { dragPreview, onBarPointerDown } = useTaskDrag<boolean, Preview>({ gridRef, rowAttr: 'data-row', targetFor, onDrop: finishDrag, focusSelector: (d) => focusOf(d.task.id), selection });
  const duplicateOne = useDuplicate(fail, useCallback((id: string) => refocus(focusOf(id)), []));
  const group = useGroupActions(selection, fail, { duplicate: duplicateOne, backlog: actions.backlog, remove: actions.remove });
  // быстрая задача: неделя под курсором, свободный трек, этот проект и подпроект; Ctrl+Enter — та же заготовка в карточку
  const quickDraft = useCallback((h: NewTaskHint, title: string, assignees: string[]) => {
    const row = latest.current.rows.find((r) => r.key === h.rowKey);
    return row ? { ...newDraft(row.project_id, row.subproject_id, h.start_date), title, assignees } : null;
  }, []);
  const quickFor = useCallback((h: NewTaskHint): QuickTask => ({
    hint: h,
    onCancel: () => setQuick(null),
    onFull: (title, assignees) => { setQuick(null); const d = quickDraft(h, title, assignees); if (d) setDraft(d); },
    onCreate: (title, assignees) => {
      const d = quickDraft(h, title, assignees);
      setQuick(null);
      if (!d) return;
      const { taskId, changes } = planNewTask(d, rowTasksNow(h.rowKey), () => crypto.randomUUID());
      void data.applyChanges(changes, { kind: 'create_task', task_id: taskId }).then((r) => { if (!r.ok) fail(r.error); else refocus(focusOf(taskId)); });
    },
  }), [quickDraft, setDraft, fail]);

  const newTaskInFirstRow = (inBacklog = false) => { const p = rows.find((r) => !r.header); if (p) setDraft(newDraft(p.project_id, p.subproject_id, startWithin(range), inBacklog)); };
  const menuFor = useCallback((t: Task): MenuItem[] => taskMenu(t, { openTask, pin: actions.pin, highlight: spotlight, group, fromBacklog: true, canWrite }), [openTask, group, actions, spotlight, canWrite]);
  const onBarKey = useTaskKeys<null>({
    openTask, duplicate: group.duplicate, pin: actions.pin, remove: group.remove, fail, focusSelector: (id) => focusOf(id), groupOf: selection.groupOf, planGroup: roadmapGroupPlan,
    plan: (taskId, { days, rows: dRows, grow }) => {
      const t = taskNow(taskId);
      if (!t) return null;
      const key = rowKeyOf(t.project_id, t.subproject_id);
      if (grow !== 0) return { planned: { changes: planResize(t, 'end', keyEnd(t.end_date, grow), rowTasksNow(key)), op: { kind: 'resize_task', task_id: t.id, side: 'end' } }, ctx: null };
      const allRows = latest.current.rows;
      const idx = allRows.findIndex((r) => r.key === key);
      const row = allRows[Math.max(0, Math.min(allRows.length - 1, idx + dRows))] ?? allRows[idx];
      if (!row) return null;
      return {
        planned: {
          changes: planMove(t, { project_id: row.project_id, subproject_id: row.subproject_id, start_date: keyStart(t.start_date, days), track: dRows === 0 ? (t.track ?? 0) : 0 }, rowTasksNow(row.key)),
          op: { kind: 'move_task', task_id: t.id },
        },
        ctx: null,
      };
    },
  });

  // ---------------------------------------------------------------- отрисовка

  if (status !== 'ready') return <ScreenLoading />;

  const groupShift = groupShiftOf(dragPreview);
  const marks: BarMarks = { colors, names, attention, pendingIds, questions: questionCounts, selected: selection.ids };
  const look: BarLook = { barH: BAR_HEIGHT, wrap: false, badges, releaseMarks, days: !!dragPreview?.day, holidays, spot: spotlight.range, spotBox: spotBox(spotlight.range, grid) };
  const projectOrder = [...new Set(rows.map((r) => r.project_id))];
  const withSubs = new Set(rows.filter((r) => r.isSub).map((r) => r.project_id));
  const splitRows: SplitRow[] = rows.map((row) => {
    const tasks = visibleTasksOf(row.key);
    const slots = visibleSlots(tasks);
    const barH = barHOf(row.key);
    const isOver = dragPreview?.overRow === row.key && dragPreview.kind === 'move';
    const hinted = hint?.rowKey === row.key;
    const quickHere = quick?.rowKey === row.key;
    const { tracks, height, headerOnly } = projectRowShape({ isSub: row.isSub, taskCount: tasks.length, slots, isOver, hintTrack: hinted ? hint.track : quickHere ? quick.track : null, withSubs: withSubs.has(row.project_id), barH });
    return {
      key: row.key,
      height,
      group: row.project_id,
      sticky: !row.isSub && headerOnly, // заголовок проекта без задач держится у верха, пока прокручиваются его подпроекты
      className: `border-b border-gray-200 ${row.isSub ? '' : 'border-t border-t-gray-300'} ${rowDrag?.id === row.project_id ? 'opacity-50' : ''}`,
      style: row.header ? { background: `${row.color}1f` } : undefined, // проект с подпроектами — заголовок в своём цвете, как дорожка лонча
      label: (
        <RowLabel
          row={row} active={hoverBlock === row.project_id} onHover={setHoverBlock}
          onAdd={() => setDraft(newDraft(row.project_id, row.subproject_id, startWithin(range)))}
          onEdit={() => editProject(row.project_id)} onHide={() => setHidden([...hidden, row.project_id])}
          onHandle={(e) => onHandlePointerDown(row.project_id, e)}
        />
      ),
      labelClassName: row.header ? '' : row.isSub ? 'bg-gray-50' : 'bg-gray-100',
      gridAttrs: row.header && tasks.length === 0 ? undefined : { 'data-row': row.key }, // без data-row заголовок не принимает бросок; старые задачи на нём ещё можно вытащить
      gridClassName: row.header ? '' : isOver ? 'bg-blue-50' : row.isSub ? 'bg-white' : 'bg-gray-50',
      grid: (
        <RowGrid
          row={row} tasks={tasks} slots={slots} tracks={tracks} grid={grid} todayX={todayX} marks={marks} look={{ ...look, barH, wrap: barH > BAR_HEIGHT }}
          drag={dragPreview} groupShift={groupShift} hint={hinted ? hint : null} onHint={setHint}
          quick={quickHere ? quickFor(quick) : null} onQuickAt={setQuick}
          onBarPointerDown={onBarPointerDown} onOpen={openTask} onKey={onBarKey} menuFor={menuFor}
          onNewAt={(date) => setDraft(newDraft(row.project_id, row.subproject_id, date))}
        />
      ),
    };
  });

  // перестановка проекта: на месте приземления строка-призрак с его именем, как контур у задачи
  const dragged = rowDrag ? projectsTable.get(rowDrag.id) : undefined;
  const ghostAt = ghostRowIndex(rowDrag, projectOrder, (id) => rows.findIndex((r) => r.project_id === id), rows.length);
  const bodyRows = dragged ? withGhostRow(splitRows, ghostAt, reorderGhostRow(dragged.name, dragged.color)) : splitRows;

  return (
    <div className="flex h-full gap-4">
      <div className="flex-1 flex flex-col min-w-0 bg-white rounded-lg shadow border border-gray-300 overflow-hidden">
        <ScreenToolbar
          actions={<WriteOnly>
            <Button size="sm" onClick={() => newTaskInFirstRow()}>+ Task</Button>
            <Button size="sm" variant="secondary" onClick={() => setProjectDraft(newProjectDraft(lanesTable.values()))}>+ Project</Button>
          </WriteOnly>}
          when={<RangeControls range={range} onChange={setRange} onReset={() => setRange(null)} />}
          view={<ViewMenu items={barViewItems({ fullTitles: [fullTitles, setFullTitles], badges: [badges, setBadges], releaseMarks: [releaseMarks, setReleaseMarks] })} order={{ differs: order.differs, onReset: () => void order.reset() }} hidden={{ items: hiddenProjects, lanes: context.hiddenLanes, rowsLabel: 'Projects', onShow: (id) => setHidden(hidden.filter((x) => x !== id)), onShowLane: (id) => setHiddenLanes(hiddenLanes.filter((k) => k !== id)), onShowAll: () => { setHidden([]); setHiddenLanes([]); } }} />}
          status={<><SelectionChip selection={selection} /><SpotlightChip spotlight={spotlight} grid={grid} onJump={jumpById} /></>}
          side={!side && <SideTabs tab={side} onTab={setSide} backlog={backlog} design={design} agenda={agenda} />}
        />
        <SplitGrid
          gridRef={gridRef} grid={grid} labelWidth={LABEL_WIDTH} cornerLabel="Project"
          rows={bodyRows} scrollKey="projects" cursor={dragPreview || rowDrag ? 'grabbing' : undefined} context={context}
        />
      </div>

      <SidePanel
        tab={side} onTab={setSide} backlog={backlog} design={design} agenda={agenda} resolved={resolved} agendaMode={agendaMode} onAgendaMode={setAgendaMode} collapsed={collapsed} colors={colors} projectNameOf={projectNameOf}
        onOpenQuestions={(t) => openTask(t, 'questions')}
        onAdd={() => newTaskInFirstRow(true)}
        onOpen={openTask}
        onPointerDown={(t, e) => onBarPointerDown(t, true, 'move', e)}
        dropTarget={!!dragPreview && dragPreview.overBacklog && !dragPreview.origin}
        menuFor={menuFor} onJump={jump}
      />

      {draft && <TaskModal draft={draft} allTasks={allTasks} onClose={closeModal} initialTab={cardTab} />}
      {eventDraft && <EventModal draft={eventDraft} onClose={() => setEventDraft(null)} />}
      {projectDraft && <ProjectModal draft={projectDraft} onClose={() => setProjectDraft(null)} />}
      {vacDraft && <VacationModal draft={vacDraft} onClose={() => setVacDraft(null)} />}
      {marquee}
    </div>
  );
}
